import { ArrowSquareOutIcon } from "@phosphor-icons/react/dist/csr/ArrowSquareOut";
import { InfoWithLabel } from "@volca/ui/components/ui/info-with-label";
import { SimpleSidebarHeader } from "@volca/ui/components/ui/simple-sidebar";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { useReportCurrency } from "@/profitability/currency";
import { showValue } from "@/profitability/missing-value";
import { ActualVsEstimate } from "@/profitability/records/estimate-variance";
import {
  rowVariance,
  withoutShownEstimates,
} from "@/profitability/records/estimates";
import { ReportMetadata } from "@/profitability/report-presentation";
import { RowComposition, moneyRoles } from "./row-composition";
import type { OpenRowDetail } from "./row-detail-context";
import { columnLabel, formatReportValue, reportLink } from "./utils";

const NUMERIC = ["number", "currency", "percent"];

/**
 * One row of a report, opened in the page's sidebar beside the dashboard, the
 * same slot the report chat uses. The script says what names the row and which
 * fields sit under which heading; without a declaration every column is shown
 * in the order the dataset declared it.
 */
export const RowDetail = ({
  open: { row, dataset, detail, headline },
  onClose,
}: {
  open: OpenRowDetail;
  onClose: () => void;
}) => {
  const currency = useReportCurrency();
  const column = (key: string) =>
    dataset.columns.find((item) => item.key === key);
  // The row names itself from its first text column when the script does not
  // say which field names it.
  const titleColumn = detail
    ? column(detail.title)
    : dataset.columns.find((item) => item.type === "text");
  const named = titleColumn?.key;
  // Without a declaration the row leads with what it earned and what it cost,
  // and the rest follows. The money is the reason the row was opened, so it
  // does not sit below a list of identifiers.
  // An estimate shows beside its actual, so it is not listed again.
  const numeric = withoutShownEstimates(
    dataset.columns.filter((item) => NUMERIC.includes(item.type)),
  ).map((item) => item.key);
  const lead = (headline ?? []).filter((key) => numeric.includes(key));
  const groups = detail
    ? detail.groups
    : [
        {
          title: "",
          fields: [...lead, ...numeric.filter((key) => !lead.includes(key))],
        },
        {
          title: "",
          // Not the column that names the row: the header is already showing
          // it, and a panel that says the same name twice in a row reads as a
          // mistake.
          fields: dataset.columns
            .filter((item) => !NUMERIC.includes(item.type))
            .map((item) => item.key)
            .filter((key) => key !== named),
        },
      ].filter((group) => group.fields.length);
  const titleKey = detail?.title ?? titleColumn?.key;
  const kind = titleColumn ? columnLabel(titleColumn) : undefined;
  const title =
    titleColumn && titleKey
      ? formatReportValue(row[titleKey], titleColumn, currency)
      : "Details";
  const subtitleColumn = detail?.subtitle ? column(detail.subtitle) : undefined;
  const subtitle =
    subtitleColumn && detail?.subtitle
      ? formatReportValue(row[detail.subtitle], subtitleColumn, currency)
      : undefined;
  const description = [kind, subtitle].filter((part): part is string => !!part);
  // A bar needs something to divide. A dataset that marks a total and a result
  // but no costs, such as a two-row summary of the business, would draw one
  // segment filling the whole width, which says nothing.
  const roles = moneyRoles(dataset);
  const hasComposition =
    !!roles.total && roles.costs.some((item) => Number(row[item.key] ?? 0) > 0);
  return (
    <Stack
      align="stretch"
      className="h-full min-h-0 w-full overflow-hidden"
      gap={0}
    >
      {/* The side panel's one header. A long name is cut to one line here;
          the full text is in its tooltip. */}
      <SimpleSidebarHeader
        title={<span title={title}>{title}</span>}
        description={
          description.length ? (
            <ReportMetadata>{description}</ReportMetadata>
          ) : undefined
        }
        onClose={onClose}
      />
      <Stack
        gap={8}
        align="stretch"
        className="min-h-0 w-full flex-1 overflow-y-auto px-5 py-6"
      >
        {hasComposition && <RowComposition row={row} dataset={dataset} />}
        {groups.map((group, index) => (
          <Stack
            className="w-full"
            key={`${group.title}-${index}`}
            gap={4}
            align="stretch"
          >
            {!!group.title && (
              <Text weight="semibold" variant="caption" tone="muted">
                {group.title}
              </Text>
            )}
            <div className="grid grid-cols-[repeat(auto-fit,minmax(9rem,1fr))] gap-x-4 gap-y-4">
              {group.fields.map((field) => {
                const item = column(field);
                if (!item) return null;
                const link = reportLink(row[field]);
                const estimated = NUMERIC.includes(item.type)
                  ? rowVariance(dataset.columns, item, row)
                  : null;
                return (
                  <InfoWithLabel key={field} label={columnLabel(item)}>
                    {link ? (
                      <a
                        href={link}
                        target="_blank"
                        rel="noreferrer"
                        className="flex w-fit items-center gap-1 text-sm text-link hover:underline"
                      >
                        Open
                        <ArrowSquareOutIcon className="size-3.5" />
                      </a>
                    ) : estimated ? (
                      <Text>
                        <ActualVsEstimate
                          variance={estimated.variance}
                          column={item}
                          currency={currency}
                        />
                      </Text>
                    ) : (
                      <Text
                        className={cn("wrap-break-word", {
                          "text-danger":
                            NUMERIC.includes(item.type) &&
                            Number(row[field]) < 0,
                        })}
                      >
                        {showValue(
                          formatReportValue(row[field], item, currency),
                          item,
                        ) || "None"}
                      </Text>
                    )}
                  </InfoWithLabel>
                );
              })}
            </div>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};
